import { useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router';
import { dayCount } from '@/shared/ui/DateRangePicker';
import { track } from '@/shared/analytics';
import { parseFilterParams, toFilterParams, type PerformanceFilters } from './filter-params';

/** `YYYY-MM-DD` for the local today, used to resolve presets. */
function todayIso(): string {
  const now = new Date();
  return new Date(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()))
    .toISOString()
    .slice(0, 10);
}

/**
 * True when two filter lists represent the same SET of members, regardless of the
 * array reference, order, or duplicates. `next.platforms` (etc.) is always a freshly
 * built array, so comparing by reference (`!==`) is always true and would fire an
 * analytics event on every filter change, even one that didn't touch that list.
 *
 * Compared as sets rather than length-plus-membership: these filters are conceptually
 * unordered sets, and a length-plus-membership check is fooled by duplicates — e.g.
 * `['dv360', 'dv360']` vs `['dv360', 'meta_ads']` has equal length and every element of
 * the first appears in the second, but the actual member sets differ.
 */
function sameMembers(a: string[], b: string[]): boolean {
  const setA = new Set(a);
  const setB = new Set(b);
  return setA.size === setB.size && [...setA].every((item) => setB.has(item));
}

// eslint-disable-next-line i18next/no-literal-string -- URL search-param names, not copy
const FILTER_OWNED_KEYS = ['preset', 'from', 'to', 'platforms', 'channels', 'campaigns'];

/**
 * Filters live in the URL so a view is shareable and survives a reload.
 *
 * Analytics fires here rather than in each control, so a filter change is reported once
 * with its resulting state instead of once per keystroke inside a multi-select.
 *
 * Filters share the query string with `performance-scope`'s `campaign`/`adset` params, so
 * writing filters must merge into whatever is already there via the functional
 * `setSearchParams` updater rather than replacing the whole query string — otherwise
 * changing a date range or a platform would silently discard the scope the user drilled
 * into. Only the filter-owned keys above are cleared before writing; everything else,
 * notably `campaign`/`adset`, is left exactly as found.
 */
export function usePerformanceFilters(projectId: string): {
  filters: PerformanceFilters;
  setFilters: (next: PerformanceFilters) => void;
} {
  const [searchParams, setSearchParams] = useSearchParams();
  const today = todayIso();

  const filters = useMemo(() => parseFilterParams(searchParams, today), [searchParams, today]);

  const setFilters = useCallback(
    (next: PerformanceFilters) => {
      if (next.preset !== filters.preset || next.range.startDate !== filters.range.startDate) {
        track('performance_date_range_changed', {
          project_id: projectId,
          preset: next.preset,
          day_count: dayCount(next.range),
        });
      }
      if (!sameMembers(next.platforms, filters.platforms)) {
        track('performance_filters_changed', {
          project_id: projectId,
          filter: 'platform',
          selected_count: next.platforms.length,
        });
      }
      if (!sameMembers(next.channels, filters.channels)) {
        track('performance_filters_changed', {
          project_id: projectId,
          filter: 'channel',
          selected_count: next.channels.length,
        });
      }
      if (!sameMembers(next.campaignIds, filters.campaignIds)) {
        track('performance_filters_changed', {
          project_id: projectId,
          filter: 'campaign',
          selected_count: next.campaignIds.length,
        });
      }

      // `replace` so filtering does not fill the back button with every intermediate state.
      setSearchParams(
        (prev) => {
          const merged = new URLSearchParams(prev);
          for (const key of FILTER_OWNED_KEYS) merged.delete(key);
          for (const [key, value] of Object.entries(toFilterParams(next))) {
            merged.set(key, value);
          }
          return merged;
        },
        { replace: true },
      );
    },
    [filters, projectId, setSearchParams],
  );

  return { filters, setFilters };
}
